import axios from "axios";
import { autorun } from "mobx";
import {
  UserStore,
  GlobalStore,
  //   CardsStore,
  //   WalletStore,
  //   StakesStore,
} from "stores";
import { parseJwt } from "helpers/funcs";
import {
  stepperConfig,
  AuthResponse,
  authSteps,
  StepPayloads,
  authStepUpScopes,
} from "./types/auth";
// import { flattenObject } from "helpers/funcs";
// import type {
// UserInterface,
//   UserLevelFlow,
//   TelegramWebAppData
// } from "helpers/types";
import type {
  RegisterCredentials,
  LoginCredentials,
  VerifyEmailCredentials,
  //   ChangePasswordCredentials,
  //   CondirmEmailCredentials,
  //   JuniorPersonalData,
  //   ReferralResponse,
  //   Toggle2FaVerificationData,
  ChangePasswordReq,
  //   ChangePhoneNumberReq,
  //   ReferralLink,
  TelegramAuthCredentials,
  //   UserLevelsInfoRes,
  //   UserPersonalData,
} from "./types/user";

export const token = {
  set(token: string) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    localStorage.setItem("accessToken", token);
  },
  setRefreshToken(refreshToken: string) {
    localStorage.setItem("refreshToken", refreshToken);
  },
  unset() {
    axios.defaults.headers.common.Authorization = "";
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  },
  setCompanyToken(token: string) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    localStorage.setItem("companyAccess", token);
  },
};

export const logIn = async (credentials: LoginCredentials) => {
  GlobalStore.startLoading();
  return axios
    .post<AuthResponse>("/auth/email/login", credentials)
    .then(({ data }) => {
      if (data.nextStep) {
        if (data.sessionToken) {
          token.set(data.sessionToken);
        }
        UserStore.setAuthStep(data.nextStep);
        return;
      }
      UserStore.setAuthStep(null);
      if (data.accessToken) {
        token.set(data.accessToken);
      }
      if (data.refreshToken) {
        token.setRefreshToken(data.refreshToken);
      }
      UserStore.setAuthenticated();
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    })
    .finally(() => GlobalStore.endLoading());
};

export const register = async (credentials: RegisterCredentials) => {
  GlobalStore.startLoading();

  const registerCredentials: RegisterCredentials = {
    ...credentials,
    source: "FNT",
  };

  return axios
    .post<AuthResponse>("/user/register", registerCredentials)
    .then(({ data }) => {
      if (data.nextStep) {
        if (data.sessionToken) {
          token.set(data.sessionToken);
        }
        UserStore.setAuthStep(data.nextStep);
        return;
      }
      UserStore.setAuthStep(null);
      if (data.accessToken) {
        token.set(data.accessToken);
      }
      if (data.refreshToken) {
        token.setRefreshToken(data.refreshToken);
      }
      UserStore.setAuthenticated();
      return true;
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    })
    .finally(() => GlobalStore.endLoading());
};

export const refreshSession = async (): Promise<string> => {
  const refreshToken = localStorage.getItem("refreshToken");

  if (!refreshToken) {
    UserStore.logOut();
    return Promise.reject("No refresh token");
  }

  try {
    const { data } = await axios.post<AuthResponse>(
      "/auth/refresh",
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );

    if (!data.accessToken) {
      return Promise.reject("No new access token");
    }

    token.set(data.accessToken);

    if (data.refreshToken) {
      token.setRefreshToken(data.refreshToken);
    }

    UserStore.setAuthenticated();

    return data.accessToken;
  } catch (err: any) {
    UserStore.logOut();
    if (err?.response?.data?.message) {
      GlobalStore.setError(err.response.data.message);
    }
    return Promise.reject(err);
  }
};

export const addAuthEmail = async (credentials: VerifyEmailCredentials) =>
  axios.post<AuthResponse>("/auth/email/add", credentials);

export const resetPasswordByEmail = async (
  credentials: VerifyEmailCredentials
) => axios.post<AuthResponse>("/auth/password/reset/email", credentials);

export const changePassword = async (data: ChangePasswordReq) =>
  axios.put<AuthResponse>("/auth/password/change", data);

export const telegramAuth = async (credentials: TelegramAuthCredentials) => {
  GlobalStore.startLoading();

  const telegramAuthCredentials: TelegramAuthCredentials = {
    ...credentials,
    source: "FNT",
  };

  return axios
    .post<AuthResponse>("/auth/telegram/login", telegramAuthCredentials)
    .then(({ data }) => {
      UserStore.setIsLoggedFromTelegram();
      localStorage.setItem("loggedFromTelegram", "true");
      if (window.Telegram?.WebApp) {
        const webApp = window.Telegram.WebApp;
        webApp.expand();
      }
      if (data.nextStep) {
        if (data.sessionToken) {
          token.set(data.sessionToken);
        }
        UserStore.setAuthStep(data.nextStep);
        return;
      }
      UserStore.setAuthStep(null);
      if (data.accessToken) {
        token.set(data.accessToken);
      }
      if (data.refreshToken) {
        token.setRefreshToken(data.refreshToken);
      }
      UserStore.setAuthenticated();
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        UserStore.setTelegramLoginError(err.response.data.message);
      }
      UserStore.openTelegramLoginErrorModal();
    })
    .finally(() => GlobalStore.endLoading());
};

export const setupMFAVerification = async () =>
  axios
    .post<AuthResponse>("/auth/mfa/setup")
    .then(({ data }) => {
      if (data.accessToken) {
        token.set(data.accessToken);
      }
      if (data.refreshToken) {
        token.setRefreshToken(data.refreshToken);
      }
      if (data.nextStep) {
        UserStore.setAuthStep(data.nextStep);
        return;
      }
      UserStore.setAuthStep(null);
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    });

export const removeMFAVerification = async () =>
  axios
    .post<AuthResponse>("/auth/mfa/remove")
    .then(({ data }) => {
      if (data.accessToken) {
        token.set(data.accessToken);
      }
      if (data.refreshToken) {
        token.setRefreshToken(data.refreshToken);
      }
      if (data.nextStep) {
        UserStore.setAuthStep(data.nextStep);
        return;
      }
      UserStore.setAuthStep(null);
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    });

export const authStepper = async <T extends authSteps>(
  step: T,
  data: StepPayloads[T]
) => {
  const config = stepperConfig[step];
  return axios
    .post<AuthResponse>(`/auth/step/${config.url}`, data)
    .then(({ data }) => {
      if (data.nextStep) {
        if (data.sessionToken) {
          token.set(data.sessionToken);
        }
        if (data.secret) {
          UserStore.setAuthSecret(data.secret);
        }
        UserStore.setAuthStep(data.nextStep);
        return;
      }
      UserStore.setAuthStep(null);
      UserStore.setAuthSecret(null);
      UserStore.setAuthOneTimeAcessToken(data.oneTimeAccessToken || null);

      if (data.accessToken) {
        token.set(data.accessToken);
        const decodedJwt = parseJwt(data.accessToken);
        if (
          decodedJwt.scope &&
          Array.isArray(decodedJwt.scope) &&
          decodedJwt.scope.includes("MFA")
        ) {
          UserStore.turnOnMFA();
        }
        if (
          decodedJwt.scope &&
          Array.isArray(decodedJwt.scope) &&
          !decodedJwt.scope.includes("MFA")
        ) {
          UserStore.turnOffMFA();
        }
      }
      if (data.refreshToken) {
        token.setRefreshToken(data.refreshToken);
      }
      UserStore.setAuthenticated();
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    });
};

export const authStepUp = async (scope: authStepUpScopes) =>
  axios
    .post<AuthResponse>("/auth/step-up", { scope })
    .then(({ data }) => {
      if (data.nextStep) {
        UserStore.setAuthStep(data.nextStep);
        return;
      }

      if (data.accessToken) {
        token.set(data.accessToken);
      }
      if (data.refreshToken) {
        token.setRefreshToken(data.refreshToken);
      }
    })
    .catch((err) => {
      if (err?.response?.data?.message) {
        GlobalStore.setError(err.response.data.message);
      }
    });

export const waitForOneTimeAccessToken = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (UserStore.authOneTimeAccessToken) {
      return resolve(UserStore.authOneTimeAccessToken);
    }

    const disposer = autorun(() => {
      if (UserStore.authOneTimeAccessToken) {
        resolve(UserStore.authOneTimeAccessToken);
        disposer(); // stop observing
      }
    });

    // optional timeout, in case user cancels/closes modal
    setTimeout(() => {
      reject(new Error("Timed out waiting for oneTimeAccessToken"));
      disposer();
    }, 5 * 60 * 1000); // 5 min
  });
};
